import { FlexibleModuleEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import SingleImageModule from '~/modules/page-builder/component/detail/single-image-module/SingleImageModule';

import DuoParagraphModule from '../duo-paragraph-module/DuoParagraphModule';
import DuoImageModule from '../duo-image-module/DuoImageModule';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import ImageParagraphModule from '../image-paragraph-module/ImageParagraphModule';
import GalleryModule from '../gallery-module/GalleryModule';
import SingleVideoModule from '../single-video-module/SingleVideoModule';
import VideoParagraphModule from '../video-paragraph-module/VideoParagraphModule';
import useOptimisticColumnTags from '~/modules/page-builder/hooks/use-optimistic-column-tags';
import QuoteModule from '../quote-module/QuoteModule';

interface DetailModuleProps {
    module: PageBuilderModuleType;
    autoPlayVideoModuleId?: string;
}

const DetailModule = ({ module, autoPlayVideoModuleId }: DetailModuleProps) => {
    const columns = module.columns;
    const meta = module.meta;

    const columnTags = useOptimisticColumnTags(
        module.columnsApprovedTags,
        module.libraryImageIds
    );

    const firstColumn = columns[0];
    if (!firstColumn) {
        return null;
    }

    switch (module.presetType) {
        case FlexibleModuleEnum.ImageParagraphModule:
            return (
                <ImageParagraphModule
                    columnsTags={columnTags}
                    columns={columns}
                />
            );
        case FlexibleModuleEnum.VideoParagraphModule:
            return <VideoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.SingleVideoModule:
            return (
                <SingleVideoModule
                    column={firstColumn}
                    shouldAutoPlayVideo={autoPlayVideoModuleId === module.id}
                />
            );
        case FlexibleModuleEnum.HeadingModule:
        case FlexibleModuleEnum.SingleParagraphModule:
            return <SingleParagraphModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.DuoParagraphModule:
            return <DuoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.QuoteModule:
            // columns[0] needs to be asserted here or upstream
            // don't know if it's reliable at this point
            return <QuoteModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.SingleImageModule:
            return (
                <SingleImageModule
                    column={firstColumn}
                    columnsTags={columnTags}
                />
            );
        case FlexibleModuleEnum.DuoImageModule:
            return (
                <DuoImageModule columnsTags={columnTags} columns={columns} />
            );
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule:
            return <GalleryModule columns={columns} />;
        default:
            return <></>;
    }
};

export default DetailModule;
